import { createFilter } from '@/pages/common/filter-dropdown/create-filter'
import {
  attach,
  combine,
  createEvent,
  createStore,
  forward,
  guard,
  restore,
  sample,
} from 'effector-root'
import { getThemesTreeListFx } from '@/features/api/subject/get-themes-tree-list'
import { DropdownItem } from '@/pages/common/types'
import { formatData } from '@/features/lib'
import {
  $selectedClass,
  classesDropdownModule,
} from '@/pages/common/dropdowns/class/classes-dropdown.model'
import {
  $selectedSubject,
  subjectsDropdownModel,
} from '@/pages/common/dropdowns/subject/subjects-dropdown.model'
import { debounce } from 'patronum'
import { getThemeData } from '@/pages/dictionary/themes/edit/parts/prerequisites/prerequisites.model'

export const getThemesTreeList = attach({
  effect: getThemesTreeListFx,
})

export const themesDropdownModule = createFilter()

export const componentMounted = createEvent<void>()
export const componentDestroyed = createEvent<void>()
export const $isComponentMounted = createStore(false)
  .on(componentMounted, () => true)
  .on(componentDestroyed, () => false)

export const $themes = createStore<DropdownItem[]>([]).reset(componentDestroyed)

export const setThemeToSelect = createEvent<DropdownItem | null>()
export const $themeToSelect = restore(setThemeToSelect, null).reset(componentDestroyed)

export const setSelectedTheme = createEvent<DropdownItem | null>()
export const $selectedTheme = restore(setSelectedTheme, null).reset(
  componentDestroyed,
  subjectsDropdownModel.methods.itemChanged,
  classesDropdownModule.methods.itemChanged
)

const themesDropdownFilled = createEvent<void>()

$themes.watch((items) => {
  items.length && themesDropdownFilled()
})

const $formToGetThemeList = combine($selectedClass, $selectedSubject, (cl, obj) => {
  return {
    study_year: cl && +cl.name,
    subject: obj && +obj.name,
    is_not_prerequisite: true,
  }
})

export const $canSetThemePosition = combine(
  $selectedSubject,
  $themes,
  (subject, themes): boolean => !!subject && !!themes.length
)

const debounced = debounce({
  source: $formToGetThemeList,
  timeout: 150,
})

guard({
  clock: debounced,
  filter: combine(
    $isComponentMounted,
    $selectedSubject,
    $selectedClass,
    (isMounted, subject, study_year) => {
      return isMounted && !!study_year?.name && !!subject?.name
    }
  ),
  target: getThemesTreeList,
})

sample({
  clock: getThemesTreeList.doneData.map(({ body }) => formatData(body)),
  target: $themes,
})

sample({
  clock: themesDropdownFilled,
  source: $themeToSelect,
  target: [
    themesDropdownModule.methods.itemChanged,
    setSelectedTheme,
    setThemeToSelect.prepend(() => null),
  ],
})

sample({
  clock: themesDropdownModule.methods.itemChanged,
  source: themesDropdownModule.store.$item,
  fn: (item: { name: string; title: string } | null) => {
    if (item) getThemeData(+item.name)
  },
})

forward({
  from: [classesDropdownModule.methods.itemChanged, subjectsDropdownModel.methods.itemChanged],
  to: themesDropdownModule.methods.resetItem.prepend(() => ({})),
})
